import React from "react";
import { api, buildWhatsAppLink } from "../lib/api";
import { toast } from "sonner";
import { useT } from "../lib/i18n";
import useSEO from "../lib/useSEO";
import { businessLd, breadcrumbLd } from "../lib/jsonld";

export default function Contact() {
  const { t } = useT();
  const [form, setForm] = React.useState({ name: "", email: "", phone: "", company: "", message: "" });
  const [submitting, setSubmitting] = React.useState(false);
  useSEO({
    title: "Contact WarehousesUAE — Dubai Warehouse & Industrial Brokerage",
    description: "Talk to WarehousesUAE.com — powered by Blueworld Realestate. WhatsApp +971 58 832 0669, email info@blueworldre.com, office at Dubai Silicon Oasis, Office #304, Donna Towers, Dubai. Saturday–Thursday, 09:00–18:00 GST.",
    canonical: window.location.origin + "/contact",
    image: window.location.origin + "/og-default.png",
    jsonLd: [businessLd(), breadcrumbLd([{ name: "Home", url: "/" }, { name: "Contact", url: "/contact" }])],
  });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await api.post("/enquiries", { ...form, source: "website" });
      toast.success(t("toast.contact_success"));
      setForm({ name: "", email: "", phone: "", company: "", message: "" });
      const url = res?.data?.whatsapp_redirect_url;
      if (url) window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      toast.error(t("toast.contact_fail"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="bg-zinc-50 min-h-screen">
      <div className="bg-zinc-950 text-white py-16 relative overflow-hidden">
        <div className="absolute inset-0 bp-grid-dark opacity-30"></div>
        <div className="relative max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="font-mono-tech text-[10px] uppercase text-[#FFCC00] mb-3">{t("contact.hero_kicker")}</div>
          <h1 className="font-display text-5xl md:text-7xl tracking-tighter leading-[0.95]">
            {t("contact.hero_title_a")}<br />{t("contact.hero_title_b")}
          </h1>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-12 grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-5 space-y-4">
          <div className="bg-white border border-zinc-200 p-6">
            <div className="font-mono-tech text-[10px] uppercase text-[#031833] mb-2">{t("contact.phone_kicker")}</div>
            <a href="tel:+971588320669" className="font-display text-2xl tracking-tighter hover:text-[#031833]">+971 58 832 0669</a>
            <div className="font-mono-tech text-xs text-zinc-500 mt-1">{t("contact.phone_hours")}</div>
          </div>
          <a href={buildWhatsAppLink("Hello WarehousesUAE, I'd like to discuss a warehouse requirement.")} className="block bg-[#25D366] text-white p-6 hover:bg-[#1eb955]">
            <div className="font-mono-tech text-[10px] uppercase opacity-80 mb-2">{t("contact.wa_kicker")}</div>
            <div className="font-display text-2xl tracking-tighter">+971 58 832 0669</div>
            <div className="font-mono-tech text-xs opacity-80 mt-1">{t("contact.wa_sub")}</div>
          </a>
          <div className="bg-white border border-zinc-200 p-6">
            <div className="font-mono-tech text-[10px] uppercase text-[#031833] mb-2">{t("contact.email_kicker")}</div>
            <a href="mailto:info@blueworldre.com" className="font-display text-xl tracking-tighter break-all hover:text-[#031833]">info@blueworldre.com</a>
          </div>
          <div className="bg-white border border-zinc-200 p-6">
            <div className="font-mono-tech text-[10px] uppercase text-[#031833] mb-2">{t("contact.office_kicker")}</div>
            <div className="text-zinc-800">{t("contact.office_addr")}</div>
            <iframe title="map" src="https://maps.google.com/maps?q=Donna+Towers+Dubai+Silicon+Oasis&t=&z=15&ie=UTF8&iwloc=&output=embed" className="w-full h-48 border-0 mt-4"></iframe>
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="bg-white border border-zinc-900 p-8" style={{ boxShadow: "8px 8px 0 0 #031833" }}>
            <div className="font-mono-tech text-[10px] uppercase text-[#031833] mb-3">{t("contact.form_kicker")}</div>
            <h2 className="font-display text-3xl tracking-tighter mb-6">{t("contact.form_title")}</h2>
            <form onSubmit={submit} data-testid="contact-form" className="space-y-4">
              <div className="grid md:grid-cols-2 gap-3">
                <input data-testid="contact-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t("contact.f_name")} className="w-full border border-zinc-300 px-3 py-3 font-mono-tech text-sm outline-none focus:border-[#031833]" />
                <input data-testid="contact-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder={t("contact.f_company")} className="w-full border border-zinc-300 px-3 py-3 font-mono-tech text-sm outline-none focus:border-[#031833]" />
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <input data-testid="contact-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={t("contact.f_email")} className="w-full border border-zinc-300 px-3 py-3 font-mono-tech text-sm outline-none focus:border-[#031833]" />
                <input data-testid="contact-phone" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder={t("contact.f_phone")} className="w-full border border-zinc-300 px-3 py-3 font-mono-tech text-sm outline-none focus:border-[#031833]" />
              </div>
              <textarea data-testid="contact-message" required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder={t("contact.f_msg")} className="w-full border border-zinc-300 px-3 py-3 font-mono-tech text-sm outline-none focus:border-[#031833]"></textarea>
              <button
                data-testid="contact-submit"
                disabled={submitting}
                className="w-full bg-zinc-950 text-white font-display text-xl tracking-tighter py-4 hover:bg-[#031833] disabled:opacity-50"
              >
                {submitting ? t("contact.submitting") : t("contact.submit")}
              </button>
              <p className="font-mono-tech text-[10px] text-zinc-500">{t("contact.privacy")}</p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
